<script setup>

import AppLayout from "@/layouts/AppLayout.vue";
import {computed, onMounted, ref} from "vue";
import {GetOrganisation, GetOrganisations} from "@/services/organisationService.js";
import {useRoute, useRouter} from "vue-router";
import {GetApp, GetAppAnalytics} from "@/services/appService.js";
import {getContext} from "@/services/context.js";
import {GetPanels} from "@/services/panelsService.js";
import {Plus} from "lucide-vue-next";
import {Skeleton} from "@/components/ui/skeleton/index.js";
import {Button} from "@/components/ui/button/index.js";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card/index.js";
import {getViewMode} from "@/services/viewModeService.js";
import ViewModePicker from "@/components/ViewModePicker.vue";

const route = useRoute();
const router = useRouter();

const organisation = ref(null);
const app = ref(null);
const panels = ref([]);
const isLoading = ref(true);
const error = ref(null);
const hasPanels = computed(() => panels.value && panels.value.length > 0);
const viewMode = ref(getViewMode());

onMounted(async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    // Load data in parallel
    const [orgData, appData, panelsData] = await Promise.all([
      GetOrganisation(getContext(route)),
      GetApp(getContext(route)),
      GetPanels(getContext(route))
    ]);
    
    organisation.value = orgData;
    app.value = appData;
    panels.value = panelsData;
  } catch (err) {
    console.error('Failed to load app data:', err);
    error.value = 'Failed to load app data. Please try again.';
  } finally {
    isLoading.value = false;
  }
});

function gotoCreate() {
  router.push({path: `/${route.params.organisationId}/app/${route.params.appId}/create`});
}

function goToEdit(panel) {
  router.push({path: `/${route.params.organisationId}/app/${route.params.appId}/panel/${panel.token}`});
}

function handleViewModeChange(mode) {
  viewMode.value = mode;
}
</script>

<template>
  <AppLayout :organisation="organisation" :app="app">
    <div class="flex-1 space-y-4">
      <div v-if="error" class="rounded-md bg-destructive/15 p-4 text-destructive">
        {{ error }}
      </div>

      <div class="flex items-center justify-between space-y-2 bg-gray-100 p-4 rounded-md" v-if="hasPanels">
        <div class="flex items-center space-x-2" v-if="hasPanels">
          <Button @click="gotoCreate" v-if="!isLoading">
            <Plus/>
            New panel
          </Button>
          <Skeleton v-if="isLoading" class="w-24 h-8"/>
        </div>
        <ViewModePicker v-model="viewMode" />
      </div>

      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4" v-if="isLoading">
        <div v-for="index in 3" :key="index" class="w-full">
          <Card>
            <CardHeader>
              <CardTitle>
                <Skeleton class="h-16"/>
              </CardTitle>
              <CardDescription>
                <Skeleton class="h-4"/>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Skeleton class="h-4 w-24 mt-2"/>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div v-if="hasPanels && !isLoading">
        <!-- Grid View -->
        <div v-if="viewMode === 'grid'" class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div v-for="panel in panels" :key="panel.id">
            <Card>
              <CardHeader>
                <CardTitle>
                  {{ panel.name }}
                </CardTitle>
                <CardDescription>
                  {{ panel.description }}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button @click="goToEdit(panel)">
                  <Plus/>
                  Edit
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <!-- List View -->
        <div v-else class="space-y-2">
          <Card v-for="panel in panels" :key="panel.id">
            <CardContent class="p-4">
              <div class="flex items-center justify-between">
                <div class="space-y-1">
                  <h3 class="font-semibold">{{ panel.name }}</h3>
                  <p class="text-sm text-muted-foreground">{{ panel.description }}</p>
                </div>
                <Button @click="goToEdit(panel)" variant="ghost" size="sm">
                  <Plus class="h-4 w-4 mr-2"/>
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div v-if="!hasPanels && !isLoading">
        <Card class="w-full mx-auto">
          <CardHeader>
            <CardTitle class="text-center">It looks like you don't have any panels yet</CardTitle>
            <CardDescription class="text-center mt-8">Shall we change that?</CardDescription>
          </CardHeader>
          <CardContent class="text-center">
            <Button
                @click="gotoCreate">
              <Plus/>
              Create one now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  </AppLayout>
</template>

<style scoped>

</style>